import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DRMT_EXCEL_LABEL } from "../constData/excelColumn";
import { getDrmExcelTableData, resetExcelTableData } from "../redux/reducers/excelDataSlice";
import { exportToExcel } from "../helpers";

export const useExportExcelTableData = () => {
  const [snackbarState, setSnackbarState] = useState({
    export_in_progress: false,
    download_started: false,
    download_completed: false,
    no_rec_to_download: false,
  });

  const [excelDataArray, setExcelDataArray] = useState([]);

  const dispatch = useDispatch();

  const { drmExcelTableData, isLoading: isLoadingExcel } = useSelector(
    (state) => state?.excelTableData
  );
  
  const { tableFilter } = useSelector((state) => state.drmFilterPayload);
  const { dailyTableFilter } = useSelector((state) => state.drmDailyFilterPayload);
  const { drmBidTableData, drmDailyTableData, isLoading: isDrmLoading } = useSelector(
      (state) => state?.drmTableData || {}
  );
  const { selectedMenu } = useSelector((state) => state.selectedMenu);
  const selectedMenuRef = useRef(selectedMenu);
    
    useEffect(() => {
      selectedMenuRef.current = selectedMenu;
    }, [selectedMenu]);
    

  const allowedKeys = useMemo(() => DRMT_EXCEL_LABEL.map((n) => ({key: n.key, name: n.name})),[]);

const requestPayload = useMemo(() => {
  if (selectedMenu === "bid_maintenance") {
    return {
      ...tableFilter,
      originCountry: tableFilter?.originCountry,
      destinationCountry: tableFilter?.destinationCountry,
    };
  } else {
    return {
      ...dailyTableFilter,
      originCountries: dailyTableFilter?.originCountries,
      destinationCountries: dailyTableFilter?.destinationCountries,
    };
  }
}, [tableFilter, dailyTableFilter, selectedMenu]);

  const setSnackbar = (key, value) => {
    setSnackbarState((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpen = (key) => snackbarState[key];
  const handleClose = (key) => setSnackbar(key, false);

  const excelDataFormatter = useCallback((excelTableData) => {
    if (!excelTableData?.drmMaintenanceDetails) return;

    const formatted = excelTableData.drmMaintenanceDetails.map((item) =>
      Object.fromEntries(
        allowedKeys.map(({key, name}) => [name, item[key] ?? ""])
      )
    );

    setExcelDataArray(formatted);
  }, [allowedKeys]);

  const handleDownloadRecodes = () => {
    const drmTableData = selectedMenuRef.current === "daily_maintenance"? (drmDailyTableData?.totalRecords || 0):(drmBidTableData?.totalRecords || 0)
    if (isLoadingExcel) {
      setSnackbar("export_in_progress", true);
    } else if (Number(drmTableData) > 0 && !isDrmLoading) {
      setSnackbar("download_started", true);

      dispatch(getDrmExcelTableData({
        reqBody: requestPayload,
        totalPages: Math.ceil((drmTableData || 0) / 1000),
        tableName: selectedMenuRef.current === "daily_maintenance" ? "drmDailyTableData" : "drmBidTableData",
      }));
    } else {
      setSnackbar("no_rec_to_download", true);
    }
  };

  const triggerExcelDownload = useCallback(() => {
    const fileName = selectedMenuRef.current === "daily_maintenance"? "DailyExcelExport": "BidExcelExport"
    if (excelDataArray.length > 0) {
      exportToExcel(excelDataArray, fileName);
      setSnackbar("download_completed", true);
      dispatch(resetExcelTableData());
      setExcelDataArray([]);
    }
  }, [excelDataArray]);

  useEffect(() => {
    if (drmExcelTableData?.drmMaintenanceDetails?.length > 0) {
      excelDataFormatter(drmExcelTableData);
    }
  }, [drmExcelTableData, excelDataFormatter]);

  useEffect(() => {
    if (excelDataArray.length > 0) {
      const timer = setTimeout(() => {
        triggerExcelDownload();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [excelDataArray, triggerExcelDownload]);

  return {
    handleDownloadRecodes,
    handleOpen,
    handleClose,
    snackbarState,
  };
};

export default useExportExcelTableData;
